import type {
    MicroFrontendAvailableApps,
    MicroFrontendRootEventsMapper,
    MicroFrontendRootPropsMapper,
    MicroFrontendsWithEvents,
    MicroFrontendsWithProps,
} from '@malt/nuxt-micro-frontend-types';
import InjectMicroFrontendAppVue from './InjectMicroFrontendApp.client.vue';

type MergePropsIntoRequiredBooleans<TProps extends Record<string, unknown>> = {
    [K in keyof TProps]-?: TProps[K] extends NonNullable<TProps[K]> ? true : false;
}[keyof TProps];

/**
 * Ensure that if at least one prop is required, the "prop" object will be required too
 */
type HaveAnyRequiredProps<
    TApp extends MicroFrontendAvailableApps,
    TProps = TApp extends keyof MicroFrontendRootPropsMapper ? MicroFrontendRootPropsMapper[TApp] : never,
> = [TProps] extends [never]
    ? false
    : [TApp] extends [MicroFrontendsWithProps]
      ? TProps extends Record<string, unknown>
          ? MergePropsIntoRequiredBooleans<TProps> extends false
              ? false
              : true
          : false
      : false;

function createInjectMicroFrontendApp() {
    type TypedMicroFrontProps<
        TApp extends MicroFrontendAvailableApps,
        TProps = MicroFrontendRootPropsMapper[Extract<TApp, MicroFrontendsWithProps>],
        TEvents = MicroFrontendRootEventsMapper[Extract<TApp, MicroFrontendsWithEvents>],
    > = {app: TApp} & ([TProps] extends [never]
        ? Record<string, unknown>
        : (HaveAnyRequiredProps<TApp> extends true
              ? {props: TProps}
              : {props?: TProps | ([TApp] extends [MicroFrontendsWithProps] ? never : Record<string, unknown>)}) &
              ([TEvents] extends [never]
                  ? object
                  : {
                        [K in keyof TEvents as `on${Capitalize<string & K>}`]?: (
                            ...args: TEvents[K] extends Array<unknown> ? TEvents[K] : []
                        ) => void;
                    }));

    type TypedInjectMicroFrontendApp = new <TApp extends MicroFrontendAvailableApps>(
        props: TypedMicroFrontProps<TApp>,
    ) => Omit<typeof InjectMicroFrontendAppVue, '$props'> & {
        $props: TypedMicroFrontProps<TApp>;
    };

    return {
        InjectMicroFrontendApp: InjectMicroFrontendAppVue as unknown as TypedInjectMicroFrontendApp,
    };
}

const {InjectMicroFrontendApp} = createInjectMicroFrontendApp();

export default InjectMicroFrontendApp;
